import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  CircularProgress
} from "@mui/material";
import { authenticationService } from "src/services";
import { get, post } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Props
========================= */
interface AddDistrictProps {
  handleClose: () => void;
  fetchDistricts: () => void;
}

const AddDistrict: React.FC<AddDistrictProps> = ({
  handleClose,
  fetchDistricts
}) => {
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  /* =========================
     State
  ========================= */
  const [states, setStates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    stateId: "",
    name: "",  // Changed from 'district' to 'name' to match backend DTO
    shortCode: "",
    isActive: true
  });

  /* =========================
     Fetch States
  ========================= */
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/state/vendor/${vendorId}/findAll`
        );
        setStates(res?.data || []);
      } catch (error) {
        console.error("Failed to fetch states", error);
        showSnackbar("Failed to load states", "error");
      }
    };

    fetchStates();
  }, [APIURL, vendorId, showSnackbar]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.stateId || !form.name) {
      showSnackbar("State and District Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: 0,
        stateId: Number(form.stateId),
        name: form.name,  // Changed from 'district' to 'name'
        shortCode: form.shortCode || null,  // Changed from 'shortCode' to 'shortCode'
        isActive: form.isActive,
        vendorId: vendorId,
        createdBy: "system" // You can pass the current user here
      };

      const res = await post(`${APIURL}/api/miscellaneous/district`, payload);
      console.log('adddistrict', res);
      
      if (res?.statusCode === 200 || res?.statusCode === "200") {
        showSnackbar(res?.message || "District added successfully", "success");
        fetchDistricts();
        handleClose();
      } else {
        showSnackbar(res?.message || "Something went wrong", "error");
      }

    } catch (error: any) {
      showSnackbar(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Add District
      </Typography>

      <Grid container spacing={2}>
        {/* State Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="State"
            name="stateId"
            value={form.stateId}
            onChange={handleChange}
          >
            {states.map(state => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* District Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="District Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortCode"
            value={form.shortCode}
            onChange={handleChange}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddDistrict;